<template>
  <BaseDialog
    type="basic"
    :is-open="isOpen"
    :title="dialogTitle"
    :description="dialogDescription"
    class="md:w-full"
    @close="emit('close')"
  >
    <div v-if="!confirmingRegenerate" class="flex flex-col pb-2">
      <LoadingMessage v-if="isLoading" />
      <div v-else class="bg-surface-container rounded-large mt-2 flex items-center gap-2 p-4">
        <p v-if="isError" class="text-body-medium text-on-surface-variant">
          {{ $t('organization.loginLessMember.accessCode.loadError') }}
        </p>
        <p v-else-if="!accessCode" class="text-body-medium text-on-surface-variant">
          {{ $t('portal.accessCode.missing') }}
        </p>
        <template v-else>
          <p class="text-headline-small grow tracking-widest break-all select-all">
            {{ formatAccessCode(accessCode) }}
          </p>
          <BaseIconButton
            v-tooltip="$t('common.copy')"
            icon="content_copy"
            @click="copyAccessCode"
          />
        </template>
      </div>
    </div>
    <template #dismiss>
      <BaseTextButton v-if="confirmingRegenerate" color="text" size="M" @click="cancelRegenerate">
        {{ $t('common.cancel') }}
      </BaseTextButton>
      <BaseTextButton v-else color="text" size="M" @click="emit('close')">{{
        $t('common.close')
      }}</BaseTextButton>
    </template>
    <template #confirm>
      <BaseTextButton
        v-if="confirmingRegenerate"
        color="tonalPrimary"
        size="M"
        :disabled="isMutating"
        @click="confirmRegenerate"
      >
        {{ $t('portal.accessCode.regenerateConfirm') }}
      </BaseTextButton>
      <BaseTextButton
        v-else-if="canChangeCode && accessCode"
        color="tonalPrimary"
        icon="refresh"
        size="M"
        @click="confirmingRegenerate = true"
      >
        {{ $t('portal.accessCode.regenerate') }}
      </BaseTextButton>
      <BaseTextButton
        v-else-if="canChangeCode"
        color="tonalPrimary"
        size="M"
        :disabled="isMutating"
        @click="regenerateAccessCode"
      >
        {{ $t('portal.accessCode.generate') }}
      </BaseTextButton>
    </template>
  </BaseDialog>
</template>

<script lang="ts" setup>
  import { computed, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import BaseDialog from '@/core/components/base/dialog/BaseDialog.vue'
  import BaseIconButton from '@/core/components/base/button/icon-button/BaseIconButton.vue'
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import LoadingMessage from '@/core/components/loading/LoadingMessage.vue'
  import { ApiRequestStatus } from '@/core/models/api/api-request-status'
  import { useSnackStore } from '@/stores/snack.store'
  import { formatAccessCode } from '@/modules/portal/utils/access-code'
  import { useMemberAccessCode } from '../composables/use-member-access-code'

  interface Props {
    isOpen: boolean
    memberId?: string
    memberName?: string
  }

  const props = defineProps<Props>()

  const emit = defineEmits<{
    close: []
  }>()

  const { t } = useI18n()
  const snackStore = useSnackStore()
  const confirmingRegenerate = ref(false)
  const { accessCode, status, regenerateStatus, loadAccessCode, regenerateAccessCode } =
    useMemberAccessCode(() => props.memberId)

  const isLoading = computed(
    () =>
      status.value === ApiRequestStatus.Idle ||
      status.value === ApiRequestStatus.Loading ||
      status.value === ApiRequestStatus.Reloading,
  )
  const isError = computed(
    () => status.value === ApiRequestStatus.Error || status.value === ApiRequestStatus.Unavailable,
  )
  const isMutating = computed(() => regenerateStatus.value === ApiRequestStatus.Loading)
  const canChangeCode = computed(
    () => status.value === ApiRequestStatus.Success && !isMutating.value,
  )

  const dialogTitle = computed(() => {
    if (confirmingRegenerate.value) {
      return t('portal.accessCode.regenerateTitle')
    }
    return t('organization.loginLessMember.accessCode.title')
  })

  const dialogDescription = computed(() => {
    if (confirmingRegenerate.value) {
      return t('portal.accessCode.regenerateDescription', { name: props.memberName ?? '' })
    }
    if (status.value !== ApiRequestStatus.Success || !props.memberName) {
      return undefined
    }
    if (!accessCode.value) {
      return t('portal.accessCode.missingDescription', { name: props.memberName })
    }
    return t('organization.loginLessMember.accessCode.description', { name: props.memberName })
  })

  watch(
    () => props.isOpen,
    async (open) => {
      confirmingRegenerate.value = false
      if (!open || !props.memberId) {
        return
      }
      await loadAccessCode()
    },
  )

  const cancelRegenerate = () => {
    confirmingRegenerate.value = false
  }

  const confirmRegenerate = async () => {
    await regenerateAccessCode()
    if (regenerateStatus.value === ApiRequestStatus.Success) {
      confirmingRegenerate.value = false
    }
  }

  const copyAccessCode = () => {
    if (!accessCode.value) {
      return
    }
    navigator.clipboard.writeText(formatAccessCode(accessCode.value))
    snackStore.showInfo(t('organization.loginLessMember.accessCode.copied'))
  }
</script>
